/**
 * ServiceRequestDetail — iter-249 (Feb 2026)
 * ==========================================
 * Individual view of a company_service_request. Shows the current stage,
 * full audit trail, and — critically — the "Advance to Next Stage" button
 * enforcing sequential progression. Moving into FEE_COLLECTION additionally
 * asks the operator to pick a payment method (issues the CGC invoice).
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, ChevronRight, FileText, ReceiptText,
  Building2, Package, CheckCircle2, Circle, Play,
  Landmark, Link as LinkIcon, Trash2,
  Upload, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { printReceipt } from '../../utils/printReceipt';
import { AgentPageShell } from '../agent/AgentPageShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  'REQUEST_RECEIVED',
  'REQUEST_ACCEPTED',
  'IN_PROGRESS',
  'FEE_COLLECTION',
  'COMPLETED_AND_DISPATCHED',
];
const STAGE_META = {
  REQUEST_RECEIVED:         { ar: 'تم استلام الطلب',       en: 'Received',       color: 'blue'    },
  REQUEST_ACCEPTED:         { ar: 'تم قبول الطلب',         en: 'Accepted',       color: 'sky'     },
  IN_PROGRESS:              { ar: 'جاري تنفيذ الطلب',     en: 'In Progress',    color: 'amber'   },
  FEE_COLLECTION:           { ar: 'تحصيل الرسوم',           en: 'Fee Collection', color: 'orange'  },
  COMPLETED_AND_DISPATCHED: { ar: 'تم الانتهاء والإرسال', en: 'Completed',      color: 'emerald' },
};
// iter-279 (Feb 2026) — CSR payment methods trimmed to bank_transfer + payment_link only.
// Cash / cheque / credit_card were retired at the user's explicit request.
const PAYMENT_METHODS = [
  { key: 'bank_transfer', icon: Landmark,    ar: 'حوالة بنكية',   en: 'Bank Transfer' },
  { key: 'payment_link',  icon: LinkIcon,    ar: 'رابط دفع',       en: 'Payment Link' },
];

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  // Fee-collection sub-form
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [pm, setPm] = useState('bank_transfer');
  const [gateway, setGateway] = useState('stripe');
  const [initialStatus, setInitialStatus] = useState('paid');
  const [note, setNote] = useState('');

  // iter-279 — Inline "paid" receipts uploader (used both in fee-collection
  // and in the completion form's "تغيير حالة الدفع" tile).
  const [paidReceipts, setPaidReceipts] = useState([]);           // [{file_id, filename, amount, ai_verified}]
  const [uploadingPaidReceipt, setUploadingPaidReceipt] = useState(false);
  const [verifyingReceipts, setVerifyingReceipts] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const paidReceiptFileRef = useRef(null);

  // iter-260 — Completion sub-form (COMPLETED_AND_DISPATCHED gates)
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [hasOutputs, setHasOutputs] = useState(null);          // null | true | false
  const [outputFiles, setOutputFiles] = useState([]);          // [{file_id, filename}]
  const [uploadingOutput, setUploadingOutput] = useState(false);
  const [bankReceiptFile, setBankReceiptFile] = useState(null); // {file_id, filename}
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const outputFileRef = useRef(null);
  const receiptFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/company-service-requests/${id}`, { headers: getAuthHeaders() });
      setRequest(r.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const currentIdx = useMemo(() => (request ? STAGES.indexOf(request.status) : -1), [request]);
  const nextStage  = currentIdx >= 0 && currentIdx < STAGES.length - 1 ? STAGES[currentIdx + 1] : null;
  const isMovingToFee = nextStage === 'FEE_COLLECTION';
  const isMovingToCompletion = nextStage === 'COMPLETED_AND_DISPATCHED';

  // iter-260 — file upload helper
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await axios.post(`${API}/upload/file`, fd, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return { file_id: r.data.id || r.data.file_id, filename: file.name };
  };

  const handleUploadOutputs = async (fileList) => {
    if (!fileList?.length) return;
    setUploadingOutput(true);
    try {
      const uploaded = [];
      for (const f of Array.from(fileList)) {
        uploaded.push(await uploadFile(f));
      }
      setOutputFiles((prev) => [...prev, ...uploaded]);
      toast.success(t(`تم رفع ${uploaded.length} ملف`, `Uploaded ${uploaded.length} file(s)`));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadingOutput(false);
      if (outputFileRef.current) outputFileRef.current.value = '';
    }
  };

  const handleUploadReceipt = async (file) => {
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const uploaded = await uploadFile(file);
      setBankReceiptFile(uploaded);
      toast.success(t('تم رفع الإيصال', 'Receipt uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadingReceipt(false);
      if (receiptFileRef.current) receiptFileRef.current.value = '';
    }
  };

  // iter-279 (Feb 2026) — Inline paid-receipts uploader (multi-file with AI verify).
  // Used both in the fee-collection sub-form ("مدفوع" branch) and in the
  // completion sub-form's "تغيير حالة الدفع" (change payment status) tile.
  const handleUploadPaidReceipts = async (fileList) => {
    if (!fileList?.length) return;
    setUploadingPaidReceipt(true);
    const nextRows = [...paidReceipts];
    try {
      for (const file of Array.from(fileList)) {
        try {
          const uploaded = await uploadFile(file);
          let amount = '';
          // Best-effort OCR autofill for images.
          if ((file.type || '').startsWith('image/')) {
            try {
              const r = await axios.post(`${API}/admin/invoices/ocr-receipt`,
                { file_id: uploaded.file_id },
                { headers: getAuthHeaders() });
              if (r.data?.amount) amount = Number(r.data.amount);
            } catch { /* ignore ocr failure */ }
          }
          nextRows.push({ file_id: uploaded.file_id, filename: uploaded.filename,
                          amount, ai_verified: false });
        } catch (e) {
          toast.error(e.response?.data?.detail || t(`فشل رفع ${file.name}`, `Failed ${file.name}`));
        }
      }
      setPaidReceipts(nextRows);
      setVerifyResult(null);
    } finally {
      setUploadingPaidReceipt(false);
      if (paidReceiptFileRef.current) paidReceiptFileRef.current.value = '';
    }
  };

  const verifyPaidReceipts = async (declaredTotal) => {
    if (!paidReceipts.length) return;
    if (paidReceipts.some(r => !r.file_id || !parseFloat(r.amount))) {
      toast.error(t('أدخل مبلغ صحيح لكل إيصال', 'Enter a valid amount for each receipt'));
      return;
    }
    setVerifyingReceipts(true);
    setVerifyResult(null);
    try {
      const r = await axios.post(`${API}/admin/invoices/verify-receipts`,
        { file_ids: paidReceipts.map(x => x.file_id), declared_total: declaredTotal },
        { headers: getAuthHeaders() });
      setVerifyResult(r.data);
      if (r.data?.ok) {
        setPaidReceipts(paidReceipts.map(x => ({ ...x, ai_verified: true })));
        toast.success(isAr ? r.data.reason_ar : r.data.reason_en);
      } else {
        toast.error(isAr ? r.data.reason_ar : r.data.reason_en);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setVerifyingReceipts(false);
    }
  };

  // Bulk-pay the CSR invoice with the uploaded receipts (called from the
  // "تغيير حالة الدفع" tile in the completion sub-form).
  const changePaymentStatus = async () => {
    if (!request?.invoice_id) {
      toast.error(t('لا توجد فاتورة مرتبطة بهذا الطلب', 'No invoice linked to this request'));
      return;
    }
    if (!paidReceipts.length) {
      toast.error(t('ارفع إيصال واحد على الأقل', 'Upload at least one receipt'));
      return;
    }
    if (paidReceipts.some(r => !r.ai_verified)) {
      toast.error(t('يجب تشغيل تحقق الذكاء الاصطناعي قبل الحفظ',
                    'AI verification must pass before saving'));
      return;
    }
    setSavingPayment(true);
    try {
      await axios.post(`${API}/admin/invoices/${request.invoice_id}/bulk-pay-receipts`, {
        method: 'bank_transfer',
        receipts: paidReceipts.map(r => ({
          file_id: r.file_id, filename: r.filename,
          amount: parseFloat(r.amount),
        })),
      }, { headers: getAuthHeaders() });
      toast.success(t('تم تحديث حالة الدفع إلى مدفوع', 'Payment status updated to paid'));
      setPaidReceipts([]);
      setVerifyResult(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSavingPayment(false);
    }
  };

  // iter-260 — Is the invoice actually paid?
  const invoiceIsPaid = request?.payment_status === 'paid';
  const requestPaymentMethod = (request?.payment_method || '').toLowerCase();
  const isOnlineMethod = ['payment_link', 'tamara', 'tabby', 'stripe', 'ziina'].includes(requestPaymentMethod);
  // iter-279 — bank_transfer is now the only manual method.
  const isManualMethod = requestPaymentMethod === 'bank_transfer';
  // Bank transfer specifically must upload a receipt if not paid
  const requiresBankReceipt = isMovingToCompletion && isManualMethod && !invoiceIsPaid;

  const advance = async () => {
    if (!nextStage) return;
    if (isMovingToFee && !showFeeForm) {
      setShowFeeForm(true);
      return;
    }
    // iter-279 — When "مدفوع" is chosen with bank_transfer, receipts MUST be AI-verified.
    if (isMovingToFee && pm === 'bank_transfer' && initialStatus === 'paid') {
      if (!paidReceipts.length) {
        toast.error(t('ارفع إيصال واحد على الأقل قبل الحفظ', 'Upload at least one receipt before saving'));
        return;
      }
      if (paidReceipts.some(r => !r.ai_verified)) {
        toast.error(t('يجب تشغيل تحقق الذكاء الاصطناعي قبل الحفظ',
                      'AI verification must pass before saving'));
        return;
      }
    }
    if (isMovingToCompletion && !showCompletionForm) {
      // iter-279 — Open the completion sub-form ALWAYS. When invoice is unpaid,
      // the form now shows a "تغيير حالة الدفع" tile (with N receipts + AI verify
      // + bulk-pay in one call). The old red-warning + hard-block flow is retired.
      // For online payment_links we still show a toast so the operator knows to
      // wait — but the completion form still opens.
      if (!invoiceIsPaid && isOnlineMethod) {
        toast.error(t(
          'رابط الدفع لم يُدفع بعد. انتظر حتى يكمل العميل الدفع قبل الانتهاء.',
          'The payment link is not paid yet. Wait until the customer pays before completing.',
        ));
      }
      setShowCompletionForm(true);
      return;
    }
    // Validation for completion
    if (isMovingToCompletion) {
      if (!invoiceIsPaid) {
        toast.error(t(
          'لا يمكن الانتهاء — الفاتورة ليست مدفوعة.',
          'Cannot complete — the invoice is not paid.',
        ));
        return;
      }
      if (hasOutputs === null) {
        toast.error(t('حدد إذا كان هناك مرفقات ناتج', 'Please answer if there are output attachments'));
        return;
      }
      if (hasOutputs === true && outputFiles.length === 0) {
        toast.error(t('ارفع مرفق واحد على الأقل', 'Upload at least one deliverable file'));
        return;
      }
      if (requiresBankReceipt && !bankReceiptFile) {
        toast.error(t('ارفع إيصال الحوالة البنكية أولاً', 'Upload the bank-transfer receipt first'));
        return;
      }
    }
    setAdvancing(true);
    try {
      const body = { to: nextStage, note: note.trim() };
      if (isMovingToFee) {
        body.payment_method = pm;
        body.gateway_provider = pm === 'payment_link' ? gateway : null;
        body.initial_payment_status = initialStatus;
        // iter-279 — Attach paid receipts so backend records payments atomically.
        if (pm === 'bank_transfer' && initialStatus === 'paid' && paidReceipts.length) {
          body.paid_receipts = paidReceipts.map(r => ({
            file_id: r.file_id, filename: r.filename,
            amount: parseFloat(r.amount),
          }));
        }
      }
      if (isMovingToCompletion) {
        body.has_outputs = !!hasOutputs;
        if (hasOutputs && outputFiles.length) {
          body.output_attachments = outputFiles;
        }
        if (bankReceiptFile) {
          body.bank_transfer_receipt = bankReceiptFile;
        }
      }
      const r = await axios.post(`${API}/admin/company-service-requests/${id}/transition`, body,
                                  { headers: getAuthHeaders() });
      toast.success(t(
        `تم الانتقال إلى: ${STAGE_META[nextStage].ar}`,
        `Advanced to: ${STAGE_META[nextStage].en}`,
      ));
      setShowFeeForm(false); setShowCompletionForm(false);
      setNote(''); setHasOutputs(null); setOutputFiles([]); setBankReceiptFile(null);
      // If we just issued the CGC invoice, print the receipt
      if (isMovingToFee && r.data?.data?.invoice_no_display) {
        const req = r.data.data;
        try {
          printReceipt({
            receipt_no: req.invoice_no_display,
            request_no: req.request_no,
            payer_name: isAr ? (req.company_name_ar || req.company_name_en) : (req.company_name_en || req.company_name_ar),
            payer_phone: '',
            payer_email: '',
            amount_aed: Number(req.price) || 0,
            currency: 'AED',
            service_name_ar: req.main_service_name_ar,
            service_name_en: req.main_service_name_en || req.main_service_name_ar,
            sub_service_name_ar: req.sub_service_name_ar,
            sub_service_name_en: req.sub_service_name_en,
            created_at: req.fee_collected_at || req.created_at,
            paid_at: initialStatus === 'paid' ? (req.fee_collected_at || req.created_at) : null,
            payment_method: { bank_transfer: 'bank_transfer', payment_link: 'link' }[pm] || 'receipt',
            status: initialStatus,
            // iter-278c — Stamp TRN snapshot from the request
            company_trn: req.company_trn || '',
          }, { language });
        } catch { /* noop */ }
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setAdvancing(false);
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'خدمات الشركات المسجلة', label_en: 'Registered Companies Services', path: '/registered-companies-services' },
    { label_ar: 'متابعة الطلبات', label_en: 'Tracking', path: '/registered-companies-services/tracking' },
    { label_ar: request?.request_no || 'الطلب', label_en: request?.request_no || 'Request', path: null },
  ];

  if (loading) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services/tracking')}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        </div>
      </AgentPageShell>
    );
  }
  if (!request) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services/tracking')}>
        <div className="flex items-center justify-center py-24 text-white/60">
          {t('الطلب غير موجود', 'Request not found')}
        </div>
      </AgentPageShell>
    );
  }

  const meta = STAGE_META[request.status] || {};

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services/tracking')}>
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="csr-detail-title">
                {isAr ? (request.main_service_name_ar || request.main_service_name_en) : (request.main_service_name_en || request.main_service_name_ar)}
                {request.sub_service_name_ar && (
                  <span className="text-white/60"> — {isAr ? request.sub_service_name_ar : request.sub_service_name_en}</span>
                )}
              </h1>
              <p className="text-[12px] text-white/50 font-mono mt-1">
                {request.request_no}
                {request.source === 'merchant_platform' && (
                  <span className="ms-2 inline-block text-[10px] px-1.5 py-0.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-sans" data-testid="csr-detail-source-merchant">
                    {isAr ? `من منصة التجار — ${request.requested_by_email || ''}` : `From merchants platform — ${request.requested_by_email || ''}`}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(t('حذف الطلب نهائياً؟ (سيُحذف الإيصال إن وُجد)', 'Delete this request permanently? (Linked invoice will be removed)'))) return;
                try {
                  await axios.delete(`${API}/admin/company-service-requests/${id}`, { headers: getAuthHeaders() });
                  toast.success(t('تم الحذف', 'Deleted'));
                  navigate('/registered-companies-services/tracking');
                } catch (err) {
                  toast.error(err.response?.data?.detail || err.message);
                }
              }}
              className="text-[11px] px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 flex items-center gap-1"
              data-testid="csr-detail-delete"
            >
              <Trash2 className="h-3 w-3" />
              {t('حذف', 'Delete')}
            </button>
          </div>

          {/* Stepper */}
          <div className="mb-6 bg-white/[0.02] border border-white/10 rounded-2xl p-5">
            <ol className="flex items-center justify-between gap-2 overflow-x-auto" data-testid="csr-stepper">
              {STAGES.map((s, idx) => {
                const sMeta = STAGE_META[s];
                const done = idx < currentIdx;
                const active = idx === currentIdx;
                return (
                  <li key={s} className="flex-1 flex items-center min-w-[110px]">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 transition-all
                        ${done ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                          : active ? `bg-${sMeta.color}-500/20 border-${sMeta.color}-500 text-white`
                          : 'bg-white/5 border-white/10 text-white/30'}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
                      </div>
                      <span className={`text-[10px] mt-1 text-center max-w-[120px]
                        ${done || active ? 'text-white' : 'text-white/40'}`}>
                        {isAr ? sMeta.ar : sMeta.en}
                      </span>
                    </div>
                    {idx < STAGES.length - 1 && (
                      <ChevronRight className={`h-3 w-3 mx-1 ${done ? 'text-emerald-500' : 'text-white/20'} ${isAr ? 'rotate-180' : ''}`} />
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Info + advance */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-emerald-400" />
                  <h2 className="text-sm font-semibold">{t('الشركة', 'Company')}</h2>
                </div>
                <p className="text-sm text-white">
                  {isAr ? (request.company_name_ar || request.company_name_en) : (request.company_name_en || request.company_name_ar)}
                </p>
                <p className="text-[11px] text-white/50">{request.company_license_no || '—'}</p>
              </section>

              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-emerald-400" />
                  <h2 className="text-sm font-semibold">{t('تفاصيل الخدمة', 'Service Details')}</h2>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-[12px]">
                  <Info label={t('السعر', 'Price')} value={`${Number(request.price || 0).toLocaleString(isAr ? 'ar-EG' : 'en-US', { minimumFractionDigits: 2 })} AED`} />
                  <Info label={t('الفاتورة', 'Invoice')} value={request.invoice_no_display || '—'} mono />
                  <Info label={t('طريقة الدفع', 'Method')} value={request.payment_method || '—'} />
                  <Info label={t('حالة الدفع', 'Payment Status')} value={request.payment_status || '—'} />
                </dl>
                {request.notes && (
                  <div className="mt-3 p-3 bg-black/40 rounded-lg border border-white/5 text-[12px] text-white/70">
                    {request.notes}
                  </div>
                )}
                {(request.attachments || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {(request.attachments || []).map((a) => (
                      <li key={a.file_id} className="flex items-center gap-2 text-[11px] text-white/70">
                        <FileText className="h-3 w-3 text-white/40" />
                        {a.filename}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Audit trail */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ReceiptText className="h-4 w-4 text-cyan-400" />
                  <h2 className="text-sm font-semibold">{t('سجل الأحداث', 'Audit Trail')}</h2>
                </div>
                <ul className="space-y-2">
                  {(request.audit_trail || []).map((e, i) => {
                    const toMeta = STAGE_META[e.to] || { ar: e.to, en: e.to };
                    return (
                      <li key={i} className="flex items-start gap-2 text-[12px] bg-black/30 rounded-lg px-3 py-2 border border-white/5"
                          data-testid={`audit-entry-${i}`}>
                        <div className="h-2 w-2 rounded-full bg-emerald-400 mt-1.5" />
                        <div className="flex-1">
                          <p className="text-white/85">
                            {e.from ? `${STAGE_META[e.from]?.[isAr ? 'ar' : 'en'] || e.from} → ` : ''}
                            <span className="font-semibold">{isAr ? toMeta.ar : toMeta.en}</span>
                          </p>
                          <p className="text-[10px] text-white/40" dir="ltr">
                            {new Date(e.at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')} · {e.operator}
                          </p>
                          {e.note && <p className="text-[11px] text-white/60 mt-0.5">"{e.note}"</p>}
                          {e.invoice_no_display && (
                            <p className="text-[10px] text-emerald-300 mt-0.5 font-mono">{e.invoice_no_display}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            {/* Advance action */}
            <div>
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 sticky top-6">
                <h2 className="text-sm font-semibold mb-2">{t('المرحلة الحالية', 'Current Stage')}</h2>
                <p className={`inline-block px-2 py-1 rounded border text-[11px] mb-4
                  ${meta.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                    : meta.color === 'orange' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                    : meta.color === 'amber' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                    : meta.color === 'sky' ? 'bg-sky-500/20 text-sky-300 border-sky-500/30'
                    : 'bg-blue-500/20 text-blue-300 border-blue-500/30'}`}>
                  {isAr ? meta.ar : meta.en}
                </p>

                {nextStage ? (
                  <>
                    <p className="text-[11px] text-white/50 mb-2">
                      {t('المرحلة التالية:', 'Next stage:')} <span className="text-white font-semibold">{isAr ? STAGE_META[nextStage].ar : STAGE_META[nextStage].en}</span>
                    </p>

                    {isMovingToFee && showFeeForm && (
                      <div className="mb-3 p-3 rounded-lg bg-orange-500/5 border border-orange-500/20 space-y-2">
                        <p className="text-[11px] text-orange-200">
                          {t('اختر طريقة الدفع لإصدار الفاتورة CGC:', 'Pick a payment method to issue the CGC invoice:')}
                        </p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {PAYMENT_METHODS.map((m) => {
                            const active = pm === m.key;
                            const Icon = m.icon;
                            return (
                              <button key={m.key} onClick={() => setPm(m.key)}
                                      className={`flex items-center gap-1 py-1.5 px-2 rounded-md border text-[11px]
                                        ${active ? 'border-orange-400/60 bg-orange-500/10 text-orange-200'
                                          : 'border-white/10 bg-black/30 text-white/70 hover:bg-white/5'}`}
                                      data-testid={`csr-fee-pm-${m.key}`}>
                                <Icon className="h-3 w-3" />
                                {isAr ? m.ar : m.en}
                              </button>
                            );
                          })}
                        </div>
                        {pm === 'payment_link' && (
                          <select value={gateway} onChange={(e) => setGateway(e.target.value)}
                                  className="w-full h-9 bg-[#0a0a0a] border border-white/10 rounded-md px-2 text-white text-xs"
                                  data-testid="csr-fee-gateway">
                            <option value="stripe">Stripe</option>
                            <option value="ziina">Ziina</option>
                            <option value="tabby">Tabby</option>
                            <option value="tamara">Tamara</option>
                          </select>
                        )}
                        {pm === 'bank_transfer' && (
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { key: 'paid',   ar: 'مدفوع',    en: 'Paid'   },
                              { key: 'unpaid', ar: 'غير مدفوع', en: 'Unpaid' },
                            ].map((s) => (
                              <button key={s.key} onClick={() => setInitialStatus(s.key)}
                                      className={`py-1.5 rounded-md border text-[11px]
                                        ${initialStatus === s.key
                                          ? (s.key === 'paid' ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-200'
                                                              : 'border-red-400/60 bg-red-500/10 text-red-200')
                                          : 'border-white/10 bg-black/30 text-white/70 hover:bg-white/5'}`}
                                      data-testid={`csr-fee-status-${s.key}`}>
                                {isAr ? s.ar : s.en}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* iter-279 — Inline paid receipts uploader (bank_transfer + paid).
                            User must upload N receipts whose sum equals the CSR price exactly. */}
                        {pm === 'bank_transfer' && initialStatus === 'paid' && (
                          <PaidReceiptsInline
                            declaredTotal={Number(request?.price) || 0}
                            receipts={paidReceipts}
                            setReceipts={setPaidReceipts}
                            uploading={uploadingPaidReceipt}
                            verifying={verifyingReceipts}
                            verifyResult={verifyResult}
                            fileRef={paidReceiptFileRef}
                            onUpload={handleUploadPaidReceipts}
                            onVerify={() => verifyPaidReceipts(Number(request?.price) || 0)}
                            t={t} isAr={isAr}
                          />
                        )}
                      </div>
                    )}

                    {/* iter-260 — Completion sub-form (COMPLETED_AND_DISPATCHED gate) */}
                    {isMovingToCompletion && showCompletionForm && (
                      <div className="mb-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-3"
                           data-testid="csr-completion-form">
                        {/* iter-279 — "Change payment status" tile replaces the old red warning.
                             Shows invoice # + current status + inline receipts uploader + AI verify. */}
                        {!invoiceIsPaid && (
                          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/25 space-y-3"
                               data-testid="csr-change-payment-tile">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-[10px] text-white/50">
                                  {t('رقم الإيصال', 'Invoice #')}
                                </p>
                                <p className="text-sm font-mono text-white/90">
                                  {request?.invoice_no_display || '—'}
                                </p>
                              </div>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-500/30">
                                {t('غير مدفوع', 'Unpaid')}
                              </span>
                            </div>
                            <p className="text-[11px] text-white/60">
                              {t('غيّر حالة الدفع برفع إيصالات تُساوي إجمالي الفاتورة تماماً، وسيتم التحقق بالذكاء الاصطناعي.',
                                 'Change the payment status by uploading receipts totalling the invoice exactly — AI will verify.')}
                            </p>
                            <PaidReceiptsInline
                              declaredTotal={Number(request?.price) || 0}
                              receipts={paidReceipts}
                              setReceipts={setPaidReceipts}
                              uploading={uploadingPaidReceipt}
                              verifying={verifyingReceipts}
                              verifyResult={verifyResult}
                              fileRef={paidReceiptFileRef}
                              onUpload={handleUploadPaidReceipts}
                              onVerify={() => verifyPaidReceipts(Number(request?.price) || 0)}
                              t={t} isAr={isAr}
                            />
                            <Button
                              onClick={changePaymentStatus}
                              disabled={savingPayment
                                        || !paidReceipts.length
                                        || paidReceipts.some(r => !r.ai_verified)}
                              className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="csr-change-payment-btn"
                            >
                              {savingPayment
                                ? <><Loader2 className="h-3.5 w-3.5 me-1 animate-spin" />{t('جارٍ الحفظ…', 'Saving…')}</>
                                : t('تغيير حالة الدفع إلى مدفوع', 'Change payment status to Paid')}
                            </Button>
                          </div>
                        )}

                        {/* iter-279 — old single-receipt uploader retired.
                             Payment status now changes via the "تغيير حالة الدفع" tile above,
                             which does N receipts + AI verify + bulk pay in one atomic call. */}

                        {/* Outputs question */}
                        <div>
                          <p className="text-[11px] text-emerald-200 mb-1.5 font-semibold">
                            {t('هل توجد مرفقات ناتج لهذه الخدمة؟', 'Are there output attachments for this service?')}
                          </p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { key: true,  ar: 'نعم', en: 'Yes' },
                              { key: false, ar: 'لا',  en: 'No' },
                            ].map((o) => (
                              <button key={String(o.key)} onClick={() => { setHasOutputs(o.key); if (!o.key) setOutputFiles([]); }}
                                      className={`py-1.5 rounded-md border text-[11px]
                                        ${hasOutputs === o.key
                                          ? (o.key ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-200'
                                                   : 'border-white/40 bg-white/5 text-white')
                                          : 'border-white/10 bg-black/30 text-white/70 hover:bg-white/5'}`}
                                      data-testid={`csr-has-outputs-${o.key}`}>
                                {isAr ? o.ar : o.en}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Output files uploader */}
                        {hasOutputs === true && (
                          <div>
                            <p className="text-[11px] text-emerald-200 mb-1.5">
                              {t('ارفع مرفقات الناتج:', 'Upload deliverables:')}
                            </p>
                            {outputFiles.map((f, i) => (
                              <div key={i} className="flex items-center gap-2 p-1.5 rounded-md bg-white/[0.03] border border-white/5 text-[11px] mb-1"
                                   data-testid={`csr-output-file-${i}`}>
                                <FileText className="h-3 w-3 text-white/60 shrink-0" />
                                <span className="flex-1 truncate text-white/80">{f.filename}</span>
                                <button onClick={() => setOutputFiles((p) => p.filter((_, j) => j !== i))}
                                        className="text-red-400 hover:text-red-300">
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => outputFileRef.current?.click()}
                              disabled={uploadingOutput}
                              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md border border-dashed border-emerald-400/40 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 text-[11px]"
                              data-testid="csr-output-upload-btn"
                            >
                              {uploadingOutput ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                              {t('إضافة مرفقات', 'Add attachments')}
                            </button>
                            <input ref={outputFileRef} type="file" hidden multiple
                                   accept="*/*"
                                   onChange={(e) => handleUploadOutputs(e.target.files)} />
                          </div>
                        )}
                      </div>
                    )}

                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-2 py-1.5 text-white text-xs mb-2"
                              placeholder={t('ملاحظة انتقال (اختياري)…', 'Transition note (optional)…')}
                              dir={isAr ? 'rtl' : 'ltr'} data-testid="csr-transition-note" />

                    <Button onClick={advance}
                            disabled={advancing || (isMovingToCompletion && showCompletionForm && !invoiceIsPaid)}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="csr-advance-btn">
                      {advancing ? <Loader2 className="h-4 w-4 me-1 animate-spin" /> : <Play className="h-4 w-4 me-1" />}
                      {isMovingToFee && !showFeeForm
                        ? t('انتقال إلى تحصيل الرسوم', 'Advance to Fee Collection')
                        : isMovingToCompletion && !showCompletionForm
                        ? t('تم الانتهاء والإرسال', 'Complete & Dispatch')
                        : isMovingToCompletion && !invoiceIsPaid
                        ? t('غيّر حالة الدفع أولاً', 'Change payment status first')
                        : isMovingToCompletion
                        ? t('تأكيد الانتهاء والإرسال', 'Confirm Complete & Dispatch')
                        : t('انتقال للمرحلة التالية', 'Advance to Next Stage')}
                    </Button>
                  </>
                ) : (
                  <p className="text-[11px] text-emerald-300 text-center py-3">
                    {t('الطلب مكتمل ✓', 'Request completed ✓')}
                  </p>
                )}
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </AgentPageShell>
  );
}

const Info = ({ label, value, mono = false }) => (
  <div>
    <p className="text-[10px] text-white/40">{label}</p>
    <p className={`text-white/85 ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);


// ─────────────────────────────────────────────────────────────────
// iter-279 (Feb 2026) — Inline paid-receipts uploader.
// Renders inside both the fee-collection sub-form ("مدفوع" branch) and
// the completion sub-form's "تغيير حالة الدفع" tile. Handles:
//   • Multi-file upload (image or PDF).
//   • Per-row editable amount (autofilled from single-file OCR).
//   • AI Verify button → /api/admin/invoices/verify-receipts
//     (server-side Gemini OCR sum vs declared total, tolerance ±0.01).
// Parent owns state (receipts, verifying, verifyResult) — this is a pure view.
// ─────────────────────────────────────────────────────────────────
function PaidReceiptsInline({
  declaredTotal, receipts, setReceipts,
  uploading, verifying, verifyResult,
  fileRef, onUpload, onVerify, t, isAr,
}) {
  const sum = (receipts || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const remaining = Math.round((declaredTotal - sum) * 100) / 100;
  return (
    <div className="p-2 rounded-md bg-emerald-500/[0.04] border border-emerald-500/20 space-y-2"
         data-testid="csr-paid-receipts-inline">
      <p className="text-[11px] text-emerald-200 font-semibold">
        {t('إيصالات الدفع (المجموع يجب أن يُساوي إجمالي الفاتورة تماماً)',
           'Payment receipts (sum must equal invoice total exactly)') + ' *'}
      </p>
      <input ref={fileRef} type="file" multiple hidden
             accept="image/*,application/pdf"
             onChange={(e) => onUpload(e.target.files)} />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-dashed border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-200 hover:bg-emerald-500/[0.12] text-[11px] disabled:opacity-50"
        data-testid="csr-paid-receipts-add-btn"
      >
        {uploading
          ? <><Loader2 className="h-3 w-3 animate-spin" />{t('جارٍ الرفع…', 'Uploading…')}</>
          : <><Upload className="h-3 w-3" />{t('إضافة إيصال / إيصالات', 'Add receipt(s)')}</>}
      </button>
      {receipts?.length > 0 && (
        <ul className="space-y-1.5" data-testid="csr-paid-receipts-list">
          {receipts.map((r, idx) => (
            <li
              key={r.file_id || idx}
              className={`p-1.5 rounded-md border text-[11px]
                          ${r.ai_verified ? 'bg-emerald-500/[0.08] border-emerald-500/40'
                                          : 'bg-black/30 border-white/10'}`}
              data-testid={`csr-paid-receipt-row-${idx}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className={`h-3 w-3 ${r.ai_verified ? 'text-emerald-300' : 'text-white/60'}`} />
                <span className="flex-1 truncate text-white/85">{r.filename}</span>
                {r.ai_verified && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 flex items-center gap-0.5">
                    <CheckCircle2 className="h-2.5 w-2.5" />{t('مُتحقّق', 'OK')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => { setReceipts(receipts.filter((_, i) => i !== idx)); }}
                  className="text-red-300 hover:text-red-200"
                  data-testid={`csr-paid-receipt-remove-${idx}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-white/50">
                  {t('المبلغ', 'Amount')}
                </label>
                <input
                  type="number" step="0.01" min="0"
                  value={r.amount}
                  onChange={(e) => setReceipts(receipts.map((x, i) =>
                    i === idx ? { ...x, amount: e.target.value, ai_verified: false } : x))}
                  className="w-24 bg-[#050505] border border-white/10 rounded px-1.5 py-0.5 text-white text-[11px] text-end"
                  dir="ltr"
                  data-testid={`csr-paid-receipt-amount-${idx}`}
                />
                <span className="text-[10px] text-white/40">AED</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {receipts?.length > 0 && (
        <div className="pt-1.5 border-t border-white/10 space-y-1">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-white/60">{t('المجموع', 'Sum')}</span>
            <span className="font-mono text-white">{sum.toFixed(2)} AED</span>
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-white/60">{t('المطلوب', 'Required')}</span>
            <span className="font-mono text-white">{declaredTotal.toFixed(2)} AED</span>
          </div>
          <div className={`flex items-center justify-between text-[11px] font-semibold
                          ${Math.abs(remaining) < 0.01 ? 'text-emerald-300'
                                                      : (remaining > 0 ? 'text-amber-300' : 'text-red-300')}`}>
            <span>{Math.abs(remaining) < 0.01 ? t('مطابق', 'Match')
                                              : (remaining > 0 ? t('المتبقّي', 'Remaining')
                                                              : t('زائد', 'Over'))}</span>
            <span className="font-mono">{Math.abs(remaining).toFixed(2)} AED</span>
          </div>
          <Button
            type="button"
            onClick={onVerify}
            disabled={verifying || !receipts.every(r => parseFloat(r.amount) > 0)}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] py-1.5 disabled:opacity-40"
            data-testid="csr-paid-receipts-verify-btn"
          >
            {verifying
              ? <><Loader2 className="h-3 w-3 me-1 animate-spin" />{t('جارٍ التحقق…', 'Verifying…')}</>
              : t('تحقق بالذكاء الاصطناعي', 'AI Verify')}
          </Button>
          {verifyResult && (
            <div className={`p-1.5 rounded-md text-[10px] border
                          ${verifyResult.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                                            : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
                 data-testid="csr-paid-receipts-verify-result">
              {isAr ? verifyResult.reason_ar : verifyResult.reason_en}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
